import React, { FC, useState } from 'react';
import { format } from 'date-fns';
import TableWithColumnSelector, { OnClickFn, TableType } from '../../core/TableWithColumnSelector';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useQuery } from '@apollo/client';
import { physicianUtilizationAnalyticsQuery, physicianUtilizationAnalyticsTotalsQuery } from '../../../graph/dashboard';
import { PhysicianUtilizationTableData, PhysicianUtilizationTableTotalsData } from '../../../types/Analytics';
import { getMinutes } from './util';
import PhysicianPatientsAnalytics from './PhysicianPatientsAnalytics';
import Button from '@material-ui/core/Button';
import { ArrowBack } from '@material-ui/icons';
import { TimelineColumnLegend } from './components/ColumnLegend';
import { CircularProgress, LinearProgress } from '@material-ui/core';
import { useScope } from '../../HospitalInfo';
import PieChart, { PieChartData } from './v2/PieChart';
import imagePIPO from '../../../assets/images/illustrations/info/PIPO.svg';
import imagePOPI from '../../../assets/images/illustrations/info/POPI.svg';
import poToCut from '../../../assets/images/illustrations/info/po_to_cut.svg';
import startToClose from '../../../assets/images/illustrations/info/start_to_close.svg';

const toTableData = (
  data: PhysicianUtilizationTableData[],
  totals: PhysicianUtilizationTableTotalsData,
  onClick?: OnClickFn,
  hasPostOp?: boolean,
): TableType => {
  const postOpHeader = hasPostOp ? ['POSTOP Avg.'] : [];
  const baseHeaders = [
    'Physician',
    'Procedures',
    'Wheels-In, Wheels-Out',
    'Physician Turnover',
    'Start to Close',
    'PO to Cut',
    'OR Daily Usage Time',
    'Aggregate Daily Patients OR Time',
    'Physician OR Utilization Index',
    'WR Avg.',
    'Pre OP Avg.',
    'Recovery Avg.',
    ...postOpHeader,
    'Pre OP to Exit Avg.',
    'Pre Op to Exit Total',
    'Surgery Days',
  ];

  const pipoOverE2EPercentage = totals?.pipoOverE2EPercentage?.toFixed(2)
    ? `${totals?.pipoOverE2EPercentage?.toFixed(2)}%`
    : '-';
  const total = [
    totals?.physician || 'Total',
    totals?.numberOfProcedures?.toString() || '-',
    getMinutes(totals?.pipoAvg),
    getMinutes(totals?.popiPhysicianAvg),
    getMinutes(totals?.startToCloseAvg),
    getMinutes(totals?.poToCutAvg),
    getMinutes(totals?.firstPIToLastPOTotal),
    getMinutes(totals?.pipoTotal),
    pipoOverE2EPercentage,
    getMinutes(totals?.wrAvg),
    getMinutes(totals?.preOpAvg),
    getMinutes(totals?.recoveryAvg),
    ...(hasPostOp ? [getMinutes(totals?.postOpAvg)] : []),
    getMinutes(totals?.preOpToExitAvg),
    getMinutes(totals?.preOpToExitTotal),
    totals?.surgeryDays?.toString(),
  ];

  const totalRow = {
    id: 'total',
    columns: total,
  };

  return {
    configHeader: [...baseHeaders],
    headers: [
      {
        id: 'h1',
        columns: [
          'Physician',
          'Procedures',
          {
            text: 'Wheels-In, Wheels-Out',
            InfoComponent: () => (
              <TimelineColumnLegend
                graphic={imagePIPO}
                title="Wheels-In, Wheels-Out"
                description='Average operating room time for patients under the care of the same physician. Operating room time is "wheels-in, wheels-out".'
              />
            ),
          },
          {
            text: 'Physician Turnover',
            InfoComponent: () => (
              <TimelineColumnLegend
                graphic={imagePOPI}
                title="Physician Turnover"
                description="Time of Patient Out of OR to next patient in OR, by surgeon"
              />
            ),
          },
          {
            text: 'Start to Close',
            InfoComponent: () => (
              <TimelineColumnLegend
                graphic={startToClose}
                title="Start to Close"
                description="Time from case start to start of closing"
              />
            ),
          },
          {
            text: 'PO to cut',
            InfoComponent: () => (
              <TimelineColumnLegend
                graphic={poToCut}
                title="PO to cut"
                description="Time from surgeons previous cases patient out of room, to time of next procedures start time."
              />
            ),
          },
          {
            text: 'OR Daily Usage Time',
            InfoComponent: () => (
              <Box p={2} width="30ch">
                <Typography variant="body2" color="textSecondary">
                  Time from the first patient entering the operating room to the time the last patient leaving the
                  operating room under the care of a specific physician.
                </Typography>
              </Box>
            ),
          },
          {
            text: 'Aggregate Daily Patients OR Time',
            InfoComponent: () => (
              <Box p={2} width="30ch">
                <Typography variant="body2" color="textSecondary">
                  Aggregate operating room time for all patients under the care of the same physician. Operating room
                  time is "wheels-in, wheels-out".
                </Typography>
              </Box>
            ),
          },
          {
            text: 'Physician OR Utilization Index',
            InfoComponent: () => (
              <Box p={2} width="30ch">
                <Typography variant="body2" color="textSecondary">
                  The ratio between Aggregate Daily Patients OR Time and OR Daily Usage Time.
                </Typography>
              </Box>
            ),
          },
          'WR Avg.',
          'Pre OP Avg.',
          'Recovery Avg.',
          ...postOpHeader,
          'Pre OP to Exit Avg.',
          'Pre Op to Exit Total',
          'Surgery Days',
        ],
      },
    ],
    rows: (data || [])
      .map(e => ({
        id: e?.id.toString(),
        columns: [
          e.physician,
          e.numberOfProcedures.toString(),
          getMinutes(e.pipoAvg),
          getMinutes(e.popiPhysicianAvg),
          getMinutes(e.startToCloseAvg),
          getMinutes(e.poToCutAvg),
          getMinutes(e.firstPIToLastPOTotal),
          getMinutes(e.pipoTotal),
          `${e.pipoOverE2EPercentage.toFixed(2)}%`,
          getMinutes(e.wrAvg),
          getMinutes(e.preOpAvg),
          getMinutes(e.recoveryAvg),
          ...(hasPostOp ? [getMinutes(e.postOpAvg)] : []),
          getMinutes(e.preOpToExitAvg),
          getMinutes(e.preOpToExitTotal),
          e.surgeryDays.toString(),
        ],
        onClick,
      }))
      .concat([totalRow]),
  };
};

const toPieChartData = (totals: PhysicianUtilizationTableTotalsData): PieChartData => ({
  subtitle: isFinite(totals.numberOfProcedures)
    ? totals.numberOfProcedures === 1
      ? '1 procedure'
      : `${totals.numberOfProcedures} procedures`
    : '-',
  inOr: totals.inOrAvg,
  inSurgery: totals.inSurgeryAvg,
  closing: totals.closingAvg,
  procedureComplete: totals.procedureCompleteAvg,
  cleaning: totals.cleaningAvg,
  orReady: totals.orReadyAvg,
});

const PhysicianUtilizationAnalytics: FC<{ filter: any; setFilter: any }> = ({ filter, setFilter }) => {
  const dateRange = filter?.dateRange?.toJSON();

  const { data, loading } = useQuery(physicianUtilizationAnalyticsQuery, {
    variables: { dateRange: { from: format(dateRange.from, 'YYYY-MM-DD'), to: format(dateRange.to, 'YYYY-MM-DD') } },
  });
  const scope = useScope();
  const hasPostOp = scope?.hospital?.modules?.hasPostop;
  const analyticsV2PieCharts = scope?.hospital?.modules?.analyticsV2PieCharts || false;
  const [physicianId, setPhysicianId] = useState<{
    idNumber: number;
    idString: string;
  } | null>(null);

  const { data: totalsData, loading: totalsLoading } = useQuery(physicianUtilizationAnalyticsTotalsQuery, {
    variables: { dateRange },
  });
  const onPhysicianClick = (physicianId: string) => {
    const id = physicianId?.split('#')?.[0];
    setPhysicianId({ idNumber: Number(id), idString: physicianId });
  };

  const table: TableType = toTableData(
    data?.physicianUtilizationAnalytics || [],
    totalsData?.physicianUtilizationAnalyticsTotals || [],
    onPhysicianClick,
    hasPostOp,
  );

  const pieChartData = toPieChartData(
    data?.physicianUtilizationAnalytics || [],
    totalsData?.physicianUtilizationAnalyticsTotals || [],
  );

  const onBack = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setPhysicianId(null);
  };

  if (physicianId) {
    return (
      <>
        <Button startIcon={<ArrowBack/>} onClick={onBack}>
          Back to Physician Utilization Analytics
        </Button>
        <PhysicianPatientsAnalytics
          physicianId={physicianId.idNumber}
          physicianName={
            (data?.physicianUtilizationAnalytics || []).find(e => e?.id === physicianId.idString)?.physician
          }
          dateRange={dateRange}
        />
      </>
    );
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" m={2} style={{ gap: '1em' }}>
        <CircularProgress size={30}/>
        <Typography variant="body1" color="textSecondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {analyticsV2PieCharts &&
        totalsData?.physicianUtilizationAnalyticsTotals && (
          <Box display="flex" alignItems="center" justifyContent="space-around" flexWrap="wrap" mb={4}>
            <Box>
              <Typography variant="subtitle2">Physician Utilization</Typography>
              <Typography variant="h1">
                {Math.round(parseInt(totalsData.physicianUtilizationAnalyticsTotals.pipoOverE2EPercentage) * 100)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                color="primary"
                value={totalsData.physicianUtilizationAnalyticsTotals.pipoOverE2EPercentage * 100}
                style={{ height: '1rem', marginTop: '.5rem' }}
              />
            </Box>
            <PieChart
              data={{
                subtitle: isFinite(totalsData.physicianUtilizationAnalyticsTotals.numberOfProcedures)
                  ? totalsData.physicianUtilizationAnalyticsTotals.numberOfProcedures === 1
                    ? '1 procedure'
                    : `${totalsData.physicianUtilizationAnalyticsTotals.numberOfProcedures} procedures`
                  : '-',
                inOr: totalsData.physicianUtilizationAnalyticsTotals.inOrAvg,
                inSurgery: totalsData.physicianUtilizationAnalyticsTotals.inSurgeryAvg,
                closing: totalsData.physicianUtilizationAnalyticsTotals.closingAvg,
                procedureComplete: totalsData.physicianUtilizationAnalyticsTotals.procedureCompleteAvg,
                cleaning: totalsData.physicianUtilizationAnalyticsTotals.cleaningAvg,
                orReady: totalsData.physicianUtilizationAnalyticsTotals.orReadyAvg,
              }}
            />
          </Box>
        )}
      <TableWithColumnSelector
        configName="physician-table-config"
        tableId="Physician-Analysis-table"
        tableName={`Analysis by Physician`}
        excelFileName={`Physician-Analysis-${format(dateRange.from, 'MM/DD/YYYY')}-${format(
          dateRange.to,
          'MM/DD/YYYY',
        )}`}
        {...table}
      />
    </>
  );
};

export default PhysicianUtilizationAnalytics;
